import React from 'react';
import SearchableSelect from '../../user-dashboard/components/SearchableSelect';

/**
 * BulkForm
 * Renders the "Add New Password Entries" card.
 *
 * Props expected (typically from usePasswordBulk):
 * - show (bool): whether to show the form
 * - onClose (): close the form (usually sets show to false + resets)
 * - entries (array): [{ user_code, site, email, password, main }]
 * - onFieldChange(index, field, value)
 * - onAddRow()
 * - onRemoveRow(index)
 * - onSubmit() : calls POST {resourcePrefix}/create-bulk
 * - invalidRows (array): [{ i, problems: ['user_code','site',...] }]
 * - hasValidationIssues (bool)
 */
export default function BulkForm({
  show,
  onClose,
  entries,
  onFieldChange,
  onAddRow,
  onRemoveRow,
  onSubmit,
  invalidRows = [],
  hasValidationIssues = false,
  sites = [], // List of available sites for dropdown
  isStaff = false, // Whether staff can add new sites
  onRequestSite = null,
}) {
  if (!show) return null;

  const problemsFor = (rowIndex) =>
    invalidRows.find((x) => x.i === rowIndex)?.problems || [];

  const isInvalid = (rowIndex, field) =>
    problemsFor(rowIndex).includes(field);

  return (
    <div className="card mb-4">
      <div className="card-header d-flex align-items-center justify-content-between">
        <h5 className="mb-0">Add New Password Entries</h5>
        {hasValidationIssues && (
          <span className="text-danger small">
            Please fix highlighted fields before saving.
          </span>
        )}
      </div>

      <div className="card-body">
        {entries.map((entry, index) => {
          const rowProblems = problemsFor(index);
          return (
            <div key={index} className="card mb-3">
              <div className="card-body">
                <div className="row g-2">
                  <div className="col-md-2">
                    <input
                      type="text"
                      className={
                        'form-control form-control-sm ' +
                        (isInvalid(index, 'user_code') ? 'is-invalid' : '')
                      }
                      placeholder="User Code"
                      value={entry.user_code}
                      onChange={(e) =>
                        onFieldChange(index, 'user_code', e.target.value)
                      }
                    />
                  </div>

                  <div className="col-md-2">
                    <SearchableSelect
                      value={entry.site}
                      onChange={(value) =>
                        onFieldChange(index, 'site', value)
                      }
                      options={sites.map(site => ({ value: site, label: site }))}
                      placeholder="Site"
                      allowCustom={isStaff}
                      onRequestSite={onRequestSite}
                      className={
                        'form-select form-select-sm ' +
                        (isInvalid(index, 'site') ? 'is-invalid' : '')
                      }
                    />
                  </div>

                  <div className="col-md-3">
                    <input
                      type="text"
                      className={
                        'form-control form-control-sm ' +
                        (isInvalid(index, 'email') ? 'is-invalid' : '')
                      }
                      placeholder="Email"
                      value={entry.email}
                      onChange={(e) =>
                        onFieldChange(index, 'email', e.target.value)
                      }
                    />
                  </div>

                  <div className="col-md-3">
                    <input
                      type="text"
                      className={
                        'form-control form-control-sm font-monospace ' +
                        (isInvalid(index, 'password') ? 'is-invalid' : '')
                      }
                      placeholder="Password"
                      value={entry.password}
                      onChange={(e) =>
                        onFieldChange(index, 'password', e.target.value)
                      }
                    />
                  </div>

                  <div className="col-md-1 d-flex align-items-center">
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={entry.main}
                        onChange={(e) =>
                          onFieldChange(index, 'main', e.target.checked)
                        }
                      />
                      <label className="form-check-label">Main</label>
                    </div>
                  </div>

                  <div className="col-md-1 d-flex align-items-center">
                    {index > 0 && (
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => onRemoveRow(index)}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>

                {/* Row-level validation hint (optional) */}
                {rowProblems.length > 0 && (
                  <div className="mt-2 small text-danger">
                    Missing: {rowProblems.join(', ')}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        <div className="mt-3">
          <button className="btn btn-secondary btn-sm me-2" onClick={onAddRow}>
            Add Another Entry
          </button>

          <button className="btn btn-primary btn-sm me-2" onClick={onSubmit}>
            Save All Entries
          </button>

          <button className="btn btn-outline-secondary btn-sm" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
